/**
 * useWidgetReadiness Hook
 *
 * Detects when the Rose widget bar is visible and ready to receive questions.
 * Used by AI Sections and other integrations that need to interact with the widget.
 *
 * This hook tracks BAR VISIBILITY, not just code readiness. The bar can be hidden due to:
 * - Traffic control (user not in display group)
 * - URL exclusion rules
 * - Device compatibility (e.g., mobile exclusion)
 * - CTA page hiding
 * - Hidden zone mode (until user clicks)
 *
 * Communication pattern:
 * 1. PULL (request-response): On mount, sends 'inboundx_request_visibility' event
 *    Widget responds with 'inboundx_bar_visible' or 'inboundx_bar_hidden'
 * 2. PUSH (notifications): Widget dispatches events when visibility changes
 *    AI Sections listens and reacts to these events
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { logger } from '../../utils/core/logger';

export interface WidgetReadinessState {
    /** Whether the widget bar is visible and ready */
    isReady: boolean;
    /** Whether we're still waiting for widget response */
    isLoading: boolean;
    /** Error message if widget detection failed */
    error: string | null;
    /** Trigger a question in the widget */
    triggerQuestion: (question: string, source?: string, context?: string) => boolean;
}

interface UseWidgetReadinessOptions {
    /** Timeout in ms before giving up on widget detection (default: 10000) */
    timeout?: number;
    /** Interval in ms between visibility requests (default: 500) */
    requestInterval?: number;
}

/**
 * Get the InboundXWidget API from window
 */
function getWidgetAPI(): {
    triggerQuestion?: (question: string, source?: string, context?: string) => boolean;
} | null {
    if (typeof window === 'undefined') return null;
    return (window as any).InboundXWidget || null;
}

/**
 * Hook to detect Rose widget readiness
 *
 * @example
 * ```tsx
 * const { isReady, isLoading, triggerQuestion } = useWidgetReadiness();
 *
 * if (isLoading) return <div>Loading...</div>;
 * if (!isReady) return <div>Widget not available</div>;
 *
 * return (
 *   <button onClick={() => triggerQuestion('How does pricing work?')}>
 *     Ask about pricing
 *   </button>
 * );
 * ```
 */
export function useWidgetReadiness(options: UseWidgetReadinessOptions = {}): WidgetReadinessState {
    const { timeout = 10000, requestInterval = 500 } = options;

    const [isReady, setIsReady] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Request visibility status from widget (pull mechanism)
    const requestVisibility = useCallback(() => {
        if (typeof window === 'undefined') return;
        logger.debug('useWidgetReadiness: Requesting visibility status from widget');
        window.dispatchEvent(new CustomEvent('inboundx_request_visibility'));
    }, []);

    // Trigger question function
    const triggerQuestion = useCallback(
        (question: string, source: string = 'ai_section_button', context?: string): boolean => {
            const api = getWidgetAPI();
            if (api?.triggerQuestion) {
                return api.triggerQuestion(question, source, context);
            }

            // Fallback: dispatch event directly, but only if widget is ready
            if (typeof window !== 'undefined' && isReady) {
                window.dispatchEvent(
                    new CustomEvent('inboundx_trigger_question', {
                        detail: { question, source, context },
                    })
                );
                return true;
            }

            logger.warn('useWidgetReadiness: Cannot trigger question - widget API not available');
            return false;
        },
        [isReady]
    );

    // Track if we've received initial response (to stop requesting)
    const hasReceivedResponse = useRef(false);
    const hasDisplayDecision = useRef(false);
    const displayAllowed = useRef<boolean | null>(null);
    const displayReason = useRef<string | undefined>(undefined);

    // Main effect: handles both push notifications and pull requests
    useEffect(() => {
        let timeoutId: ReturnType<typeof setTimeout>;
        let requestIntervalId: ReturnType<typeof setInterval>;

        const handleDisplayDecision = (
            event: Event & {
                detail?: {
                    allowed?: boolean;
                    reason?: string;
                };
            }
        ) => {
            if (typeof event.detail?.allowed !== 'boolean') {
                return;
            }

            hasDisplayDecision.current = true;
            displayAllowed.current = event.detail.allowed;
            displayReason.current = event.detail.reason;
        };

        // Handle visibility events (push notifications from widget)
        const handleVisible = () => {
            logger.debug('useWidgetReadiness: Bar VISIBLE via event');
            hasReceivedResponse.current = true;
            setIsReady(true);
            setIsLoading(false);
            setError(null);
        };

        const handleHidden = () => {
            logger.debug('useWidgetReadiness: Bar HIDDEN via event');
            hasReceivedResponse.current = true;
            setIsReady(false);
            setIsLoading(false);
            setError('Bar hidden');
        };

        // Set up visibility event listeners (push mechanism)
        window.addEventListener('inboundx_bar_visible', handleVisible);
        window.addEventListener('inboundx_bar_hidden', handleHidden);
        window.addEventListener(
            'inboundx_display_decision',
            handleDisplayDecision as EventListener
        );

        // Send initial visibility request (pull mechanism)
        // Widget will respond with inboundx_bar_visible or inboundx_bar_hidden
        requestVisibility();

        // Periodically request visibility until we get a response or timeout
        // This handles the case where widget loads after AI Sections
        requestIntervalId = setInterval(() => {
            if (!hasReceivedResponse.current) {
                requestVisibility();
            }
        }, requestInterval);

        // Timeout handler - stop requesting after timeout
        timeoutId = setTimeout(() => {
            clearInterval(requestIntervalId);
            if (!hasReceivedResponse.current) {
                if (hasDisplayDecision.current && displayAllowed.current) {
                    logger.warn('useWidgetReadiness: Widget detection timed out');
                } else if (!hasDisplayDecision.current) {
                    logger.info(
                        'useWidgetReadiness: Widget detection timed out before display decision was available'
                    );
                } else {
                    logger.debug(
                        'useWidgetReadiness: Widget detection timed out on expected blocked path',
                        { reason: displayReason.current }
                    );
                }
                setError('Widget not available');
                setIsLoading(false);
            }
        }, timeout);

        return () => {
            clearTimeout(timeoutId);
            clearInterval(requestIntervalId);
            window.removeEventListener('inboundx_bar_visible', handleVisible);
            window.removeEventListener('inboundx_bar_hidden', handleHidden);
            window.removeEventListener(
                'inboundx_display_decision',
                handleDisplayDecision as EventListener
            );
        };
    }, [requestVisibility, timeout, requestInterval]);

    return {
        isReady,
        isLoading,
        error,
        triggerQuestion,
    };
}
